/*
 * Steel Pony Farm — global "something is happening" affordance.
 *
 * The worst storefront feeling is clicking and then sitting there unsure if
 * anything actually happened. This adds one consistent busy state (a spinner +
 * a lock against double-clicks) to any control that triggers a *real wait* —
 * a page navigation or a server round-trip — marked with `data-spf-busy`.
 *
 * It deliberately does NOT touch instant / optimistic actions (picking a
 * pickup spot, the quantity steppers): those update the UI immediately, so a
 * spinner there would only make them feel slower. Indicators go where the
 * wait is real: the Checkout buttons.
 */
(function () {
  'use strict';

  var BUSY = 'spf-busy-active';

  function setBusy(el) {
    if (!el || el.classList.contains(BUSY)) return;
    el.classList.add(BUSY);
    el.setAttribute('aria-busy', 'true');
  }

  function clearBusy(el) {
    el.classList.remove(BUSY);
    el.removeAttribute('aria-busy');
  }

  // Links (e.g. the drawer's "Checkout" → cart page): the click navigates, so
  // mark busy immediately. Page unload tears it down; pageshow clears it if the
  // shopper returns via the Back button (bfcache restore).
  document.addEventListener(
    'click',
    function (e) {
      var el = e.target.closest('a[data-spf-busy]');
      if (!el) return;
      if (el.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        return;
      }
      setBusy(el);
    },
    true
  );

  // Submit buttons (e.g. the cart-page Checkout button submitting cart-form):
  // mark busy on the form's *submit* so the native submission still fires. We
  // never set `disabled` here — that would cancel the in-flight submit; the CSS
  // locks pointer-events instead. The checkout button lives outside its form
  // (associated via form="cart-form"), so fall back to a form-id lookup.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!(form instanceof HTMLFormElement)) return;
      var btn = form.querySelector('[data-spf-busy]');
      if (!btn && form.id) {
        btn = document.querySelector('[data-spf-busy][form="' + form.id + '"]');
      }
      if (btn) setBusy(btn);
    },
    true
  );

  window.addEventListener('pageshow', function () {
    var nodes = document.querySelectorAll('.' + BUSY);
    for (var i = 0; i < nodes.length; i++) clearBusy(nodes[i]);
  });

  // Spinner styles, injected once. Only ever visible after a click, so there is
  // no flash-of-unstyled risk. Uses currentColor so it matches the button text.
  var css =
    '[data-spf-busy].' + BUSY + '{position:relative;pointer-events:none;cursor:default;}' +
    '[data-spf-busy].' + BUSY + ' .button-text{visibility:hidden;}' +
    '[data-spf-busy].' + BUSY + '::after{content:"";position:absolute;top:50%;left:50%;' +
    'width:1.05em;height:1.05em;margin:-.525em 0 0 -.525em;border:2px solid currentColor;' +
    'border-right-color:transparent;border-radius:50%;animation:spf-spin .6s linear infinite;}' +
    '@keyframes spf-spin{to{transform:rotate(360deg);}}' +
    '@media (prefers-reduced-motion:reduce){[data-spf-busy].' + BUSY + '::after{animation-duration:1.4s;}}';
  var style = document.createElement('style');
  style.textContent = css;
  document.head.appendChild(style);
})();

/*
 * SPF 2026-08-20 v2 (Mike): on the free-pick picker page, items already in the cart are
 * HIDDEN outright (v1's badge left a live stepper behind and went stale after removals).
 * Membership is re-synced on every cart event, so removing an item brings its card back
 * and adding one hides it — no stale states. Presentation-layer only: the discount still
 * zeroes the cheapest eligible line (native BxGy has no not-already-in-cart condition on
 * this plan), so pool curation stays the real lever.
 */
(function () {
  'use strict';
  if (window.location.pathname.indexOf('/collections/free-pick-samplers') === -1) return;

  var style = document.createElement('style');
  style.textContent = '.spf-pick-incart{display:none !important;}';
  document.head.appendChild(style);

  var handles = {};
  var t = null;

  function apply() {
    document.querySelectorAll('.product-card').forEach(function (card) {
      var link = card.querySelector('a[href*="/products/"]');
      if (!link) return;
      var m = link.getAttribute('href').match(/\/products\/([^/?#]+)/);
      if (!m) return;
      // Hide the GRID CELL, not the card: the collection grid wraps every card in an
      // <li class="product-grid__item">, and hiding only the card left a card-sized
      // white hole in the grid (Mike's screenshot, 2026-08-24).
      var cell = card.closest('.product-grid__item') || card.closest('li') || card;
      cell.classList.toggle('spf-pick-incart', !!handles[m[1]]);
    });
  }

  function sync() {
    // cache-buster + no-store: a plain /cart.js read after a change can serve a stale
    // cached copy (same trap as the documented fetch('/cart') empty-cart cache).
    fetch('/cart.js?t=' + Date.now(), { cache: 'no-store', headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        handles = {};
        (cart.items || []).forEach(function (i) { if (i.handle) handles[i.handle] = true; });
        apply();
      })
      .catch(function () { /* never break the page over a hint */ });
  }

  sync();
  // cards can hydrate late (lazy rows) — re-apply without refetching
  [400, 1200, 2500].forEach(function (ms) { setTimeout(apply, ms); });
  // stay honest as the cart changes (add from this page, remove in the drawer, etc.)
  ['cart:update', 'cart:updated', 'cart:refresh', 'theme:cart:update'].forEach(function (ev) {
    document.addEventListener(ev, function () {
      clearTimeout(t);
      t = setTimeout(sync, 350);
    });
  });
})();

/*
 * Steel Pony Farm — release a drawer left open, and the page it froze, after a
 * Back-button restore.
 *
 * THE BUG (reported live 2026-09-04, measured on the live store 2026-09-05):
 * open the cart drawer, navigate to the cart page WITHOUT closing it, press
 * Back, and the page cannot be scrolled at all. Re-opening and closing the
 * drawer frees it. Measured state on the frozen page:
 *
 *     bodyPos: "fixed"   bodyTop: "-622.667px"   bodyWidth: "100%"
 *     openDialogs: 1     htmlH: "1222/1222"      movedProgrammatically: false
 *
 * That is `dialog.js showDialog()` verbatim — it pins the page while a drawer
 * is open and stores the scroll offset in `top`. With the body out of flow the
 * document collapses to viewport height (1222/1222), so there is genuinely
 * nowhere to scroll; even `window.scrollTo` does nothing. It is NOT input
 * being swallowed.
 *
 * WHY IT STICKS: a bfcache restore hands the page back exactly as it was and
 * replays no toggle, close, popstate or unload event. `closeDialog()` is the
 * only code that un-pins, and it never runs. The dialog is handed back with
 * `open` still set while having lost its modal/top-layer state, so it is
 * invisible — the shopper sees an ordinary page that will not move.
 *
 * WHY BOTH HALVES ARE NEEDED. Un-pinning alone leaves a dialog stranded open,
 * and `showDialog()` opens with `if (dialog.open) return;` — so the next tap on
 * the cart icon is spent silently closing an invisible drawer instead of
 * opening it. Closing it alone does not un-pin, because we deliberately do not
 * call the theme's `closeDialog()`: it awaits an animation that a restored,
 * non-modal dialog may never fire, which would hang the release. So this does
 * both itself, synchronously.
 *
 * Three earlier attempts missed because each fixed one lock and the page has
 * several. Kept together deliberately — the others are cheap, they are real,
 * and any of them can be the one stranded:
 *   - `html[scroll-lock]` (dialog.js toggle handler + base.css)
 *   - inline `overflow:hidden` on <html> (spf-fulfillment, spf-categories-drawer)
 *   - inline `overflow:hidden` on <body> too (spf-featured-modal)
 *
 * Everything here is conditional and idempotent: on a normal page load nothing
 * is set, so nothing runs.
 */
(function () {
  'use strict';

  function releaseAfterRestore() {
    var body = document.body;
    var html = document.documentElement;
    if (!body) return;

    // 1. Close any dialog the restore stranded open, so the next click on the
    //    cart icon opens the drawer instead of being spent closing it.
    //    Native .close() is synchronous and does not depend on an animation.
    var open = document.querySelectorAll('dialog[open]');
    for (var i = 0; i < open.length; i++) {
      try { open[i].close(); } catch (e) { open[i].removeAttribute('open'); }
    }

    // 1b. The featured-product promo (`.spf-fm`) is NOT a <dialog> — it is a
    //     plain div toggled by an `is-open` class — so the loop above walks
    //     straight past it. MEASURED on a real phone 2026-09-05: after a Back
    //     restore it reported `modal=OPEN` at every sample while the page was
    //     frozen, and stayed open even once the overflow lock was released.
    //     A full-screen overlay over an unlocked page looks exactly like a
    //     frozen page: taps and wheel land on the overlay, not the document.
    //     It is invisible because the restore returns the class without the
    //     paint state the opening transition would have produced.
    //     This mirrors its own close(): drop the class, then hide it.
    var promo = document.querySelectorAll('.spf-fm.is-open');
    for (var j = 0; j < promo.length; j++) {
      promo[j].classList.remove('is-open');
      promo[j].hidden = true;
    }

    // 2. Un-pin the page. `top` holds the offset the lock captured, so the
    //    value that froze the page is the one that puts the shopper back.
    if (body.style.position === 'fixed') {
      var offset = parseInt(body.style.top || '0', 10); // negative, e.g. "-622px"
      body.style.width = '';
      body.style.position = '';
      body.style.top = '';
      if (offset) window.scrollTo({ top: -offset, behavior: 'instant' });
    }

    // 3. The other three locks, in case one of those drawers was the one open.
    html.removeAttribute('scroll-lock');
    if (html.style.overflow === 'hidden') html.style.overflow = '';
    if (body.style.overflow === 'hidden') body.style.overflow = '';
  }

  // ---- WHY THE CLEANUP ALSO RUNS ON THE WAY OUT --------------------------
  // Measured on a real phone 2026-09-05, on a page that was frozen:
  //     MOVED BY CODE: YES (0->400)      scroller=html  h=38726/695
  //     body: pos=- ov=-/- ta=auto       covering: none
  //     visualViewport: scale=1.00       dlg=0  promo=hidden
  // Every lock is absent, nothing overlays the page, and code can scroll it
  // 400px on demand — yet the shopper's finger cannot move it at all.
  //
  // That is not page state, it is BROWSER state. `showModal()` puts a dialog in
  // the top layer and makes everything behind it non-interactive. Being frozen
  // into bfcache with that dialog still open, then restored, leaves that layer
  // wedged: the DOM reads perfectly healthy while touch goes nowhere. No amount
  // of clearing styles afterwards reaches it — which is why four fixes that all
  // measured "clean" still left the page dead, and why re-opening and closing
  // the drawer by hand cures it (that exits the top layer properly).
  //
  // So the cure is to never enter bfcache in that state. `pagehide` fires
  // before the page is frozen, so the drawer is closed on the way OUT and the
  // page is stored clean. The pageshow/popstate passes stay as a belt-and-
  // braces for pages already cached in a bad state.
  window.addEventListener('pagehide', releaseAfterRestore);
  window.addEventListener('pageshow', releaseAfterRestore);
  window.addEventListener('popstate', releaseAfterRestore);

  // ---- THE INVARIANT: only a MODAL may pin the page ----------------------
  // Everything above cleans up at known moments. It was not enough, and the
  // touch probe showed why. On a frozen page, 702 touchmove events arrived,
  // none were cancelled — and `h=695/695`: scrollHeight equal to clientHeight,
  // a document collapsed to one screen. That is the pinned-body signature
  // (every earlier reading with pos=fixed also showed h=695/695). The finger
  // was working perfectly; there was simply nowhere to scroll.
  //
  // The new fact in that reading was `dlg=0` — pinned with NO dialog open.
  // `showDialog()` pins the body and THEN calls showModal(); if that second
  // step never lands, or the dialog is closed behind it, the pin is orphaned
  // and nothing in the theme will ever remove it. Chasing each caller failed
  // four times, so this enforces the rule instead: the page may only be pinned
  // while something modal is genuinely open.
  //
  // Deliberately narrow: it acts ONLY when the body is pinned AND no dialog is
  // open AND the promo is not showing, so it cannot fight a legitimately open
  // drawer. Reading `body.style.position` is a cheap string compare and the
  // observer only fires when someone actually touches the attribute.
  // ---- AND: closing the drawer with X must not throw you to the top -------
  // Reported live 2026-09-05 and confirmed present WITHOUT any of this file:
  // close the drawer with the X and the page jumps to the top; press Back
  // instead and your position is kept. Two different paths explain the split.
  //
  // The drawer pushes a history entry when it opens — and it does so while the
  // page is already pinned, when window.scrollY reads 0. Tapping X ends in
  // `history.back()`, and the browser restores THAT entry's remembered scroll
  // position, which is 0, landing on top of whatever the theme itself restores.
  // Pressing Back moves to the PREVIOUS entry, which holds the real position,
  // so that path was never affected.
  //
  // `body.style.top` already carries the truth: the pin writes `-<scrollY>px`,
  // so the offset captured at pin time is readable without hooking any theme
  // code. Remember it while pinned, and if unpinning lands at the top when it
  // should not have, put the shopper back. Deliberately conservative — it only
  // corrects when the result is ~0 AND a non-zero position was recorded, so a
  // genuine scroll-to-top is never overridden.
  var pinnedAt = null;
  var suppressFocusScrollUntil = 0;

  // Make focus non-scrolling for a brief window after the drawer closes.
  // `preventScroll` keeps the focus move (accessibility intact) while stopping
  // the browser scrolling that element into view.
  try {
    var realFocus = HTMLElement.prototype.focus;
    HTMLElement.prototype.focus = function (opts) {
      if (Date.now() < suppressFocusScrollUntil) {
        var o = {};
        for (var k in (opts || {})) o[k] = opts[k];
        o.preventScroll = true;
        try { return realFocus.call(this, o); } catch (e) { /* fall through */ }
      }
      return realFocus.apply(this, arguments);
    };
  } catch (e) { /* leave native focus alone if this cannot be wrapped */ }

  // Traced with a timestamped log on a real phone, closing the drawer with X
  // from scrollY 12321:
  //     +3087ms body.position=fixed top=-12321px  (drawer opens, page pinned)
  //     +6014ms body.position=-  scrollY=12321    (theme restores, CORRECTLY)
  //     +6053ms EVENT popstate                    (the drawer's history.back())
  //     +6131ms scrollY=12039 ... +6586ms 5916 ... +6877ms scrollY=0
  //
  // The theme's own restore is right. What undoes it is the BROWSER restoring
  // the scroll position remembered for the entry the drawer pushed — an entry
  // created while the page was pinned, so its remembered position is 0. And
  // because base.css sets `scroll-behavior: smooth`, that restoration is a
  // ~750ms GLIDE rather than a jump. An earlier version of this checked at
  // 80ms, saw 12236, correctly concluded nothing was wrong, and stood down
  // while the page slid away underneath it.
  //
  // So do not race the animation — stop it being started. `scrollRestoration`
  // is switched to 'manual' for the moment of that one navigation and put
  // straight back, which leaves every other Back on the site untouched
  // (including Back from the cart page, which already worked correctly).
  // The delayed check stays as a safety net, now timed after the glide would
  // have finished.
  function notePin(b) {
    var html = document.documentElement;

    if (b.style.position === 'fixed') {
      var t = parseInt(b.style.top || '0', 10);
      if (t < 0) pinnedAt = -t;
      // Set BEFORE the drawer pushes its history entry. Setting it at close
      // time was too late — scrollRestoration is read against the entry being
      // navigated to, not the one you are leaving, which is why the earlier
      // attempt changed nothing.
      try { history.scrollRestoration = 'manual'; } catch (e) {}
      return;
    }
    if (pinnedAt == null) return;
    var want = pinnedAt;
    pinnedAt = null;

    // Three things it is NOT, each ruled out by a traced run: the browser's
    // scroll restoration (forcing 'manual' at close time changed nothing), a
    // focus() pulling the header into view (the wrapper never fired), and a
    // cancelled scroll (nothing was prevented). What is certain is the shape:
    // right after popstate the page GLIDES to exactly 0 over ~700ms.
    //
    // So rather than keep hunting the caller, take away its animation. With
    // scroll-behavior forced to auto for the close, whatever scrolls the page
    // does it in one frame instead of a second-long slide, and the correction
    // below lands immediately after — the shopper sees at most a flicker.
    var priorBehavior = html.style.scrollBehavior;
    html.style.scrollBehavior = 'auto';

    // Switching scrollRestoration to 'manual' for this navigation did NOT stop
    // the glide (traced 2026-09-05), which rules the browser's own restoration
    // out. What remains is a `.focus()` call: returning focus to the drawer's
    // trigger scrolls that element into view, the trigger lives in the header
    // at the top of the document, and `scroll-behavior: smooth` turns that into
    // a ~1s glide to exactly 0. So focus is made non-scrolling for the moment
    // of the close — the focus still moves, for keyboard and screen-reader
    // users, it just stops dragging the page with it.
    suppressFocusScrollUntil = Date.now() + 1400;

    // Re-assert the position for a short window. Whatever scrolls the page does
    // so shortly after popstate, and with the animation removed it is a single
    // jump — so a few cheap checks over ~600ms catch it and put the shopper
    // back with no visible travel. Only ever corrects a drop to the very top
    // when a real position was recorded, so a genuine scroll-to-top still works.
    // Correct BEFORE the browser paints, not on a timer. A 50ms poll left a
    // visible flash of the top of the page (reported 2026-09-07: "instantly,
    // super quick, I think it shows the very top of the page, but then it goes
    // back") because a wrong frame could paint inside the gap. requestAnimation-
    // Frame runs between the scroll landing and the paint, so the bad value
    // never reaches the screen. A scroll listener backs it up for the same
    // reason: it fires the moment the position changes rather than up to 50ms
    // later.
    var until = Date.now() + 700;

    function restore() {
      if (want > 4 && window.scrollY < 4) window.scrollTo({ top: want, behavior: 'instant' });
    }

    function frame() {
      restore();
      if (Date.now() < until) {
        requestAnimationFrame(frame);
      } else {
        window.removeEventListener('scroll', restore);
        html.style.scrollBehavior = priorBehavior;
        try { history.scrollRestoration = 'auto'; } catch (e) {}
      }
    }

    window.addEventListener('scroll', restore);
    requestAnimationFrame(frame);
  }

  try {
    var mo = new MutationObserver(function () {
      var b = document.body;
      if (!b) return;
      notePin(b);
      if (b.style.position !== 'fixed') return;
      if (document.querySelector('dialog[open]')) return;      // a real modal is up
      if (document.querySelector('.spf-fm.is-open')) return;    // the promo is up
      releaseAfterRestore();
    });
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['style'],
      subtree: true,
    });
  } catch (e) { /* no observer: the listeners above still cover the common path */ }
})();
